import { useCallback, useEffect, useRef, useState } from "react"
import type { MutableRefObject } from "react"
import AnnotationService from "services/annotations.service"
import type { WebViewerInstance } from "@pdftron/webviewer"
import type { TErefWithFurniture, TFurniture, TPageElevationData } from "types/elevationReferences.type"

// Pointer travel (px) that turns a press into a marquee drag rather than a click.
const DRAG_THRESHOLD_PX = 4

export type TConnectObjectsSession = {
	eref: TErefWithFurniture
	/** Backend 0-based page number of the eref — the only page objects can be picked from. */
	pageNumber: number
}

type TClientRect = { left: number; top: number; right: number; bottom: number }

export type ErefConnectObjectsContext = {
	instanceRef: MutableRefObject<WebViewerInstance | null>
	/**
	 * Orchestrator-owned mirror of the live session. Owned there (not here) because the canvas and
	 * sidebar handlers that must stay inert while the mode is open are declared before this hook runs.
	 */
	connectSessionRef: MutableRefObject<TConnectObjectsSession | null>
	/** Plain (non-React) marquee element from createOverlayContainers. */
	connectMarqueeElRef: MutableRefObject<HTMLElement | null>
	/** `.App` element — the origin the marquee's app-relative coordinates are measured against. */
	appContainerRef: MutableRefObject<HTMLElement | null>
	relatedObjects: TPageElevationData[] | undefined
	findFurnitureById: (furnitureId: number) => TFurniture | undefined
	findErefWithFurnitureById: (erefId: number) => TErefWithFurniture | undefined
	/** useBatchOperations.runConnectOrMove — resolves true only when the request succeeded. */
	runConnectOrMove: (targetErefId: number, furniture: TFurniture[]) => Promise<boolean>
	clearSelectionToolbar: () => void
}

const sameIds = (a: number[], b: number[]) => a.length === b.length && a.every((id, i) => id === b[i])

/**
 * Eref "Connect objects" mode (REQ-372). Opened from the eref context menu, it takes over the
 * document area: a top bar explains the gesture, the user picks objects by clicking them or by
 * dragging a marquee over them, and Connect links the picked objects to the eref.
 *
 * Three things are deliberate:
 *
 *   • It drives the real Apryse selection (`selectFurnitureMany`), so picked objects render as
 *     selected highlights — the count in the bar is just a mirror of that set.
 *   • `beginObjectPicking` / `endObjectPicking` bracket the mode: highlights can't be dragged out
 *     from under the marquee (REQ-350 would otherwise move them) and the selection takes the
 *     design's blue outline + control points, scoped to this mode only. Every exit path — cancel,
 *     a successful connect, leaving editor mode, closing the modal, unmount — must end it.
 *   • The request goes through the shared `runConnectOrMove` (the `/move/` endpoint), so an object
 *     already linked to ANOTHER eref is reassigned rather than rejected — and the cache splice,
 *     version chaining, recoloring and toast are the same ones the furniture-side flow uses. Only
 *     objects already on THIS eref are ineligible.
 */
export const useErefConnectObjects = (ctx: ErefConnectObjectsContext) => {
	const {
		instanceRef,
		connectSessionRef,
		connectMarqueeElRef,
		appContainerRef,
		relatedObjects,
		findFurnitureById,
		findErefWithFurnitureById,
		runConnectOrMove,
		clearSelectionToolbar,
	} = ctx

	const [connectSession, setConnectSession] = useState<TConnectObjectsSession | null>(null)
	const [selectedConnectIds, setSelectedConnectIds] = useState<number[]>([])
	const selectedConnectIdsRef = useRef<number[]>([])

	// Frame scheduled to run the (comparatively expensive) hit-test for the latest drag rect. The
	// rectangle itself is drawn synchronously on every mousemove; only the hit-test is coalesced.
	const hitTestFrameRef = useRef<number | null>(null)

	const cancelPendingHitTest = useCallback(() => {
		if (hitTestFrameRef.current !== null) {
			cancelAnimationFrame(hitTestFrameRef.current)
			hitTestFrameRef.current = null
		}
	}, [])

	const hideMarquee = useCallback(() => {
		const el = connectMarqueeElRef.current
		if (el) el.style.display = "none"
	}, [connectMarqueeElRef])

	// `appRect` is measured once per drag (at mousedown) and passed in: reading it per mousemove
	// forces a layout on every frame of the gesture.
	const showMarquee = useCallback(
		(rect: TClientRect, appRect: { left: number; top: number }) => {
			const el = connectMarqueeElRef.current
			if (!el) return
			el.style.left = `${rect.left - appRect.left}px`
			el.style.top = `${rect.top - appRect.top}px`
			el.style.width = `${rect.right - rect.left}px`
			el.style.height = `${rect.bottom - rect.top}px`
			el.style.display = "block"
		},
		[connectMarqueeElRef],
	)

	// The picked set lives in a ref and is mirrored onto the canvas; React state (which only drives the
	// bar's count) is pushed separately by `commitSelectionCount`.
	//
	// Splitting the two is what keeps a marquee drag smooth: mid-gesture only the highlights update —
	// no React render of the viewer's shadow trees (both sidebars included) can land between two
	// mousemoves. The count catches up when the gesture ends, which is also what the design shows
	// (the bar still reads "Click on detection or drag to select multiple" while a drag is in flight).
	const applyCanvasSelection = useCallback((ids: number[]) => {
		if (sameIds(selectedConnectIdsRef.current, ids)) return
		selectedConnectIdsRef.current = ids
		AnnotationService.updateFurnitureSelection(ids)
	}, [])

	const commitSelectionCount = useCallback(() => {
		setSelectedConnectIds((prev) => (sameIds(prev, selectedConnectIdsRef.current) ? prev : selectedConnectIdsRef.current))
	}, [])

	// Same page as the eref, and not already connected to it. Objects linked to another eref stay
	// eligible — the `/move/` endpoint reassigns them.
	const isEligible = useCallback(
		(furnitureId: number): boolean => {
			const session = connectSessionRef.current
			if (!session) return false
			const furniture = findFurnitureById(furnitureId)
			if (!furniture) return false
			if (furniture.page_number !== session.pageNumber) return false
			return furniture.eref_id !== session.eref.id
		},
		[findFurnitureById, connectSessionRef],
	)

	// Returns whether it actually cancelled a session, so the Escape hotkey knows to stop there
	// instead of falling through to "close the right sidebar".
	const cancelConnectObjects = useCallback((): boolean => {
		if (!connectSessionRef.current) return false
		connectSessionRef.current = null
		selectedConnectIdsRef.current = []
		setConnectSession(null)
		setSelectedConnectIds([])
		cancelPendingHitTest()
		hideMarquee()
		AnnotationService.endObjectPicking()
		AnnotationService.deselectAll()
		return true
	}, [hideMarquee, cancelPendingHitTest, connectSessionRef])

	const startConnectObjects = useCallback(
		(eref: TErefWithFurniture) => {
			// Any prior selection would be counted as picked objects, so start from nothing. An active
			// jump session is deliberately left alone: its bar keeps its slot at the top of the document
			// and this one stacks below it (see the overlay offset in useQuotationViewer), with its
			// navigation inert while picking.
			AnnotationService.deselectAll()
			clearSelectionToolbar()
			AnnotationService.beginObjectPicking()
			const session: TConnectObjectsSession = { eref, pageNumber: eref.page_number }
			connectSessionRef.current = session
			selectedConnectIdsRef.current = []
			setConnectSession(session)
			setSelectedConnectIds([])
			hideMarquee()
			// Deliberately no scroll-to-eref: the mode is entered from the eref's own context menu, so
			// the user is already looking at the right place and re-centering the document would only
			// move the objects they were about to pick. Picking stays restricted to the eref's page
			// either way (see isEligible).
		},
		[clearSelectionToolbar, hideMarquee, connectSessionRef],
	)

	const runConnectObjects = useCallback(async () => {
		const session = connectSessionRef.current
		if (!session) return
		const furniture = selectedConnectIdsRef.current
			.map((id) => findFurnitureById(id))
			.filter((f): f is TFurniture => !!f)
		if (furniture.length === 0) return
		const succeeded = await runConnectOrMove(session.eref.id, furniture)
		// On failure the toast is already up and the bar stays open with the selection intact so the
		// user can retry; only a success tears the mode down.
		if (succeeded) cancelConnectObjects()
	}, [findFurnitureById, runConnectOrMove, cancelConnectObjects, connectSessionRef])

	// Canvas gestures for the mode: press-drag-release draws the marquee and adds every eligible
	// object it covers to the selection; a press without travel is a click that toggles one object,
	// or clears the selection when it lands on empty space / an ineligible object. Listeners are
	// capture-phase and swallow the event so Apryse can neither pan nor start its own drag.
	useEffect(() => {
		if (!connectSession) return
		const documentViewer = instanceRef.current?.Core.documentViewer
		const scrollEl = (documentViewer?.getScrollViewElement() as HTMLElement | null) ?? null
		if (!scrollEl) return

		let start: { x: number; y: number } | null = null
		let isDragging = false
		// Selection as of the drag's start — a new marquee ADDS to what was already picked.
		let baseIds: number[] = []
		// Measured once per drag: the app container can't move mid-gesture, and reading it per
		// mousemove would force a layout on every frame.
		let appRect = { left: 0, top: 0 }
		let latestRect: TClientRect | null = null

		// Resolve which objects the current rectangle covers and mirror that into the selection. Runs
		// at most once per animation frame, so a fast drag can't queue up more work than it can draw.
		const runHitTest = () => {
			hitTestFrameRef.current = null
			if (!latestRect) return
			const hits = AnnotationService.getFurnitureIdsInClientRect(latestRect, connectSession.pageNumber).filter((id) =>
				isEligible(id),
			)
			applyCanvasSelection([...baseIds, ...hits.filter((id) => !baseIds.includes(id))])
		}

		const onMouseDown = (e: MouseEvent) => {
			if (e.button !== 0) return
			start = { x: e.clientX, y: e.clientY }
			isDragging = false
			baseIds = selectedConnectIdsRef.current
			latestRect = null
			const measured = appContainerRef.current?.getBoundingClientRect()
			appRect = { left: measured?.left ?? 0, top: measured?.top ?? 0 }
			e.preventDefault()
			e.stopPropagation()
			e.stopImmediatePropagation()
		}

		const onMouseMove = (e: MouseEvent) => {
			if (!start) return
			if (!isDragging) {
				if (Math.abs(e.clientX - start.x) < DRAG_THRESHOLD_PX && Math.abs(e.clientY - start.y) < DRAG_THRESHOLD_PX) {
					return
				}
				isDragging = true
			}
			latestRect = {
				left: Math.min(start.x, e.clientX),
				top: Math.min(start.y, e.clientY),
				right: Math.max(start.x, e.clientX),
				bottom: Math.max(start.y, e.clientY),
			}
			// The rectangle follows the cursor immediately; the hit-test rides the next frame.
			showMarquee(latestRect, appRect)
			if (hitTestFrameRef.current === null) hitTestFrameRef.current = requestAnimationFrame(runHitTest)
		}

		const onMouseUp = (e: MouseEvent) => {
			// No press of ours in flight (e.g. a click on the bar itself) — leave it alone.
			if (!start) return
			const wasDragging = isDragging
			start = null
			isDragging = false
			hideMarquee()
			if (wasDragging) {
				// Settle on the final rectangle: the last mousemove's frame may not have run yet.
				cancelPendingHitTest()
				runHitTest()
				// The gesture is over — now it's safe to let React re-render with the new count.
				commitSelectionCount()
				return
			}

			const metadata = AnnotationService.getEntityAtMouseEvent(e)
			if (metadata?.entityType === "furniture" && isEligible(metadata.entityId)) {
				const current = selectedConnectIdsRef.current
				applyCanvasSelection(
					current.includes(metadata.entityId)
						? current.filter((id) => id !== metadata.entityId)
						: [...current, metadata.entityId],
				)
				commitSelectionCount()
				return
			}
			// Empty space (or something that can't be connected) clears the picks.
			applyCanvasSelection([])
			commitSelectionCount()
		}

		scrollEl.addEventListener("mousedown", onMouseDown, true)
		// Move/up on the window so a drag that leaves the document area still tracks and ends.
		window.addEventListener("mousemove", onMouseMove, true)
		window.addEventListener("mouseup", onMouseUp, true)
		return () => {
			cancelPendingHitTest()
			scrollEl.removeEventListener("mousedown", onMouseDown, true)
			window.removeEventListener("mousemove", onMouseMove, true)
			window.removeEventListener("mouseup", onMouseUp, true)
		}
		// Escape is NOT bound here — it lives in the hotkey registry (hotkeys/useEditorHotkeys), which
		// gives it a single owner and the right precedence over "Escape closes the right sidebar".
	}, [
		connectSession,
		instanceRef,
		appContainerRef,
		showMarquee,
		hideMarquee,
		applyCanvasSelection,
		commitSelectionCount,
		isEligible,
		cancelPendingHitTest,
	])

	// Keep the session honest against cache refreshes: an eref deleted from under the mode closes
	// it, and picked objects that no longer exist (or were connected elsewhere in another tab) drop
	// out of the selection.
	useEffect(() => {
		const session = connectSessionRef.current
		if (!session || !relatedObjects) return
		if (!findErefWithFurnitureById(session.eref.id)) {
			cancelConnectObjects()
			return
		}
		const stillEligible = selectedConnectIdsRef.current.filter((id) => isEligible(id))
		if (sameIds(selectedConnectIdsRef.current, stillEligible)) return
		applyCanvasSelection(stillEligible)
		commitSelectionCount()
	}, [
		relatedObjects,
		findErefWithFurnitureById,
		isEligible,
		applyCanvasSelection,
		commitSelectionCount,
		cancelConnectObjects,
		connectSessionRef,
	])

	// Unmount safety net: never leave the mode's presentation applied behind us.
	useEffect(() => {
		return () => {
			if (!connectSessionRef.current) return
			connectSessionRef.current = null
			AnnotationService.endObjectPicking()
		}
	}, [connectSessionRef])

	return {
		connectSession,
		connectSessionRef,
		selectedConnectIds,
		startConnectObjects,
		cancelConnectObjects,
		runConnectObjects,
	}
}
